import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPhone, FaEnvelope, FaEdit, FaTrash, FaPlus, FaTimes, FaChevronDown, FaChevronRight, FaMailBulk, FaSave, FaCheck } from "react-icons/fa";
import { Base_Url } from '../config';
import './QuickContacts.css';
import { selectUserExists } from '../redux/userSlice';
import { useSelector } from 'react-redux';

const QuickContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedContacts, setExpandedContacts] = useState({});
  const [editingContact, setEditingContact] = useState(null);
  const [editingSubEmail, setEditingSubEmail] = useState(null);
  const [editFormData, setEditFormData] = useState({
    parent_text: '',
    parent_email: ''
  });
  const [subEmailFormData, setSubEmailFormData] = useState({
    text: '',
    email: ''
  });
  const [addingSubTo, setAddingSubTo] = useState(null);
  const [newSubEmail, setNewSubEmail] = useState({ text: '', email: '' });

  const userExists = useSelector(selectUserExists);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Base_Url}/parents`);
      setContacts(response.data);

      const initialExpanded = {};
      response.data.forEach(contact => {
        initialExpanded[contact.id] = false;
      });
      setExpandedContacts(initialExpanded);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const toggleContact = (contactId) => {
    setExpandedContacts(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const startEditingContact = (contact, e) => {
    e.stopPropagation();
    setEditingContact(contact.id);
    setEditFormData({
      parent_text: contact.display_name || '',
      parent_email: contact.parent_email
    });
  };

  const cancelEditContact = (e) => {
    e.stopPropagation();
    setEditingContact(null);
    setEditFormData({ parent_text: '', parent_email: '' });
  };

  const saveContactEdit = async (contactId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${Base_Url}/parent/${contactId}`, {
        parent_email: editFormData.parent_email,
        display_name: editFormData.parent_text
      });
      setEditingContact(null);
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update contact');
    }
  };

  const startEditingSubEmail = (sub, e) => {
    e.stopPropagation();
    setEditingSubEmail(sub.id);
    setSubEmailFormData({
      text: sub.title || '',
      email: sub.sub_email
    });
  };

  const cancelEditSubEmail = (e) => {
    e.stopPropagation();
    setEditingSubEmail(null);
    setSubEmailFormData({ text: '', email: '' });
  };

  const saveSubEmailEdit = async (subId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${Base_Url}/sub/${subId}`, {
        sub_email: subEmailFormData.email,
        title: subEmailFormData.text
      });
      setEditingSubEmail(null);
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update email');
    }
  };

  const handleDelete = async (contactId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this contact and all its emails?')) {
      try {
        await axios.delete(`${Base_Url}/parent/${contactId}`);
        fetchContacts();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete contact');
      }
    }
  };

  const handleDeleteSubEmail = async (subEmailId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this email?')) {
      try {
        await axios.delete(`${Base_Url}/sub/${subEmailId}`);
        fetchContacts();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete email');
      }
    }
  };

  const startAddingSubEmail = (contactId, e) => {
    e.stopPropagation();
    setAddingSubTo(contactId);
    setNewSubEmail({ text: '', email: '' });
  };

  const cancelAddingSubEmail = (e) => {
    e.stopPropagation();
    setAddingSubTo(null);
    setNewSubEmail({ text: '', email: '' });
  };

  const saveNewSubEmail = async (contactId, e) => {
    e.stopPropagation();
    if (!newSubEmail.email.trim()) return;
    
    try {
      await axios.post(`${Base_Url}/sub`, {
        parent_id: contactId,
        sub_email: newSubEmail.email.trim(),
        title: newSubEmail.text
      });
      setAddingSubTo(null);
      setNewSubEmail({ text: '', email: '' });
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add email');
    }
  };

  const addNewContact = async () => {
    const displayName = prompt('Enter display name:');
    if (!displayName) return;
    
    const email = prompt('Enter email address:');
    if (!email) return;

    try {
      await axios.post(`${Base_Url}/parent`, {
        parent_email: email,
        display_name: displayName
      });
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add contact');
    }
  };

  if (loading) {
    return (
      <div className="quick-contacts-container">
        <div className="quick-contacts-card">
          <div className="quick-contacts-loading-container">
            <div className="quick-contacts-spinner"></div>
            <div>Loading contacts...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quick-contacts-container">
        <div className="quick-contacts-card">
          <div className="quick-contacts-error-container">
            <div className="quick-contacts-error-box">
              <p className="quick-contacts-error-text">Error: {error}</p>
              <button
                className="quick-contacts-dismiss-button"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quick-contacts-container">
      <div className="quick-contacts-card">
        <div className="quick-contacts-header">
          <h4 className="quick-contacts-header-title">
            <div className="quick-contacts-icon-wrapper">
              <FaMailBulk />
            </div>
            Quick Contacts
          </h4>
          {userExists && (
            <button
              className="quick-contacts-add-button"
              onClick={addNewContact}
            >
              <div className="quick-contacts-icon-wrapper">
                <FaPlus />
              </div>
            </button>
          )}
        </div>

        <hr className="quick-contacts-divider m-0" />

        <div className="quick-contacts-body">
          {contacts.length === 0 ? (
            <div className="quick-contacts-empty-state">
              <p className="quick-contacts-empty-text">No contacts found</p>
              {userExists && (
                <button
                  className="quick-contacts-primary-button"
                  onClick={addNewContact}
                >
                  <div className="quick-contacts-icon-wrapper">
                    <FaPlus />
                  </div>
                  Add First Contact
                </button>
              )}
            </div>
          ) : (
            contacts.map((contact) => (
              <div key={contact.id} className="quick-contacts-contact-parent">
                <div
                  className="quick-contacts-contact-header"
                  style={{
                    backgroundColor: expandedContacts[contact.id] ? '#f8f9fa' : 'white'
                  }}
                  onClick={() => toggleContact(contact.id)}
                >
                  <div className="quick-contacts-contact-header-left">
                    <div className="quick-contacts-expand-icon">
                      <div className="quick-contacts-icon-wrapper">
                        {expandedContacts[contact.id] ? <FaChevronDown /> : <FaChevronRight />}
                      </div>
                    </div>
                    <div className="quick-contacts-contact-icon">
                      <div className="quick-contacts-icon-wrapper">
                        <FaEnvelope />
                      </div>
                    </div>
                    
                    {editingContact === contact.id ? (
                      <div style={{ flex: 1 }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          className="quick-contacts-inline-input"
                          value={editFormData.parent_text}
                          onChange={(e) => setEditFormData({...editFormData, parent_text: e.target.value})}
                          placeholder="Display Name"
                          style={{ marginBottom: '4px', width: '100%' }}
                        />
                        <input
                          type="email"
                          className="quick-contacts-inline-input"
                          value={editFormData.parent_email}
                          onChange={(e) => setEditFormData({...editFormData, parent_email: e.target.value})}
                          placeholder="Email Address"
                          style={{ width: '100%' }}
                        />
                      </div>
                    ) : (
                      <div>
                        <h6 className="quick-contacts-contact-title">
                          {contact.parent_email}
                        </h6>
                        <small className="quick-contacts-contact-subtitle">
                          {contact.display_name || 'Main Email'}
                        </small>
                      </div>
                    )}
                  </div>
                  
                  {userExists && (
                    <div className="quick-contacts-contact-actions">
                      {editingContact === contact.id ? (
                        <>
                          <button
                            className="quick-contacts-save-button"
                            onClick={(e) => saveContactEdit(contact.id, e)}
                            title="Save"
                          >
                            <div className="quick-contacts-icon-wrapper">
                              <FaCheck />
                            </div>
                          </button>
                          <button
                            className="quick-contacts-cancel-button-icon"
                            onClick={cancelEditContact}
                            title="Cancel"
                          >
                            <div className="quick-contacts-icon-wrapper">
                              <FaTimes />
                            </div>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="quick-contacts-edit-button"
                            onClick={(e) => startEditingContact(contact, e)}
                            title="Edit Contact"
                          >
                            <div className="quick-contacts-icon-wrapper">
                              <FaEdit />
                            </div>
                          </button>
                          <button
                            className="quick-contacts-delete-button"
                            onClick={(e) => handleDelete(contact.id, e)}
                            title="Delete Contact"
                          >
                            <div className="quick-contacts-icon-wrapper">
                              <FaTrash />
                            </div>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {expandedContacts[contact.id] && (
                  <div className="quick-contacts-contact-children">
                    {contact.sub_emails.map((sub) => (
                      <div key={sub.id} className="quick-contacts-sub-email-item">
                        {editingSubEmail === sub.id ? (
                          <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              className="quick-contacts-inline-input-small"
                              value={subEmailFormData.text}
                              onChange={(e) => setSubEmailFormData({...subEmailFormData, text: e.target.value})}
                              placeholder="Display Text"
                              style={{ flex: 1 }}
                            />
                            <input
                              type="email"
                              className="quick-contacts-inline-input-small"
                              value={subEmailFormData.email}
                              onChange={(e) => setSubEmailFormData({...subEmailFormData, email: e.target.value})}
                              placeholder="Email"
                              style={{ flex: 2 }}
                            />
                            <button
                              className="quick-contacts-save-button-small"
                              onClick={(e) => saveSubEmailEdit(sub.id, e)}
                              title="Save"
                            >
                              <div className="quick-contacts-icon-wrapper">
                                <FaCheck />
                              </div>
                            </button>
                            <button
                              className="quick-contacts-cancel-button-small"
                              onClick={cancelEditSubEmail}
                              title="Cancel"
                            >
                              <div className="quick-contacts-icon-wrapper">
                                <FaTimes />
                              </div>
                            </button>
                          </div>
                        ) : (
                          <div className="quick-contacts-email-item-content">
                            <div>
                              <small className="quick-contacts-sub-email-label">{sub.title || 'Sub Email'}:</small>
                              <span className="quick-contacts-email-value">{sub.sub_email}</span>
                            </div>
                            {userExists && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  className="quick-contacts-edit-sub-button"
                                  onClick={(e) => startEditingSubEmail(sub, e)}
                                  title="Edit Email"
                                >
                                  <div className="quick-contacts-icon-wrapper">
                                    <FaEdit />
                                  </div>
                                </button>
                                <button
                                  className="quick-contacts-delete-sub-button"
                                  onClick={(e) => handleDeleteSubEmail(sub.id, e)}
                                  title="Delete Email"
                                >
                                  <div className="quick-contacts-icon-wrapper">
                                    <FaTimes />
                                  </div>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {addingSubTo === contact.id && (
                      <div className="quick-contacts-sub-email-item" style={{ backgroundColor: '#e3f2fd' }}>
                        <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            className="quick-contacts-inline-input-small"
                            value={newSubEmail.text}
                            onChange={(e) => setNewSubEmail({...newSubEmail, text: e.target.value})}
                            placeholder="Display Text"
                            style={{ flex: 1 }}
                          />
                          <input
                            type="email"
                            className="quick-contacts-inline-input-small"
                            value={newSubEmail.email}
                            onChange={(e) => setNewSubEmail({...newSubEmail, email: e.target.value})}
                            placeholder="Email Address"
                            style={{ flex: 2 }}
                          />
                          <button
                            className="quick-contacts-save-button-small"
                            onClick={(e) => saveNewSubEmail(contact.id, e)}
                            title="Save"
                          >
                            <div className="quick-contacts-icon-wrapper">
                              <FaCheck />
                            </div>
                          </button>
                          <button
                            className="quick-contacts-cancel-button-small"
                            onClick={cancelAddingSubEmail}
                            title="Cancel"
                          >
                            <div className="quick-contacts-icon-wrapper">
                              <FaTimes />
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    {userExists && addingSubTo !== contact.id && (
                      <div className="quick-contacts-add-sub-inline">
                        <button
                          className="quick-contacts-add-sub-inline-button"
                          onClick={(e) => startAddingSubEmail(contact.id, e)}
                        >
                          <div className="quick-contacts-icon-wrapper">
                            <FaPlus />
                          </div>
                          Add Child Email
                        </button>
                      </div>
                    )}

                    {contact.sub_emails.length === 0 && addingSubTo !== contact.id && (
                      <div className="quick-contacts-empty-sub-emails">
                        <small className="quick-contacts-empty-sub-text">No child emails</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickContacts;